import type {
  LibraryFiltersModel,
  LibraryListParams,
  LibraryListResponse,
  LibrarySortField,
} from '@/js/account/entities/document/model/types';
import { isAxiosError } from 'axios';
import { onMounted, ref } from 'vue';
import { useQuerySync } from '@/js/account/shared/lib';

export const DOCUMENT_LIST_PER_PAGE = 8;

const SORT_FIELDS: readonly LibrarySortField[] = [
  'newest',
  'last_updated',
  'a_to_z',
  'z_to_a',
];

type DocumentListBrowseOptions = {
  /**
   * Whether the endpoint answers a search with the entire result set instead of a
   * page of it. Only the library index does; the downloads history paginates a
   * search like any other listing.
   */
  searchReturnsFullSet?: boolean
};

export function useDocumentListBrowse(
  fetchList: (params: LibraryListParams) => Promise<LibraryListResponse>,
  { searchReturnsFullSet = false }: DocumentListBrowseOptions = {},
) {
  const query = useQuerySync();

  const search = ref(query.read('search'));
  const sortBy = ref<LibrarySortField>(parseSort(query.read('sort')));
  const filters = ref<LibraryFiltersModel>({
    categoryIds: parseIds(query.read('categories')),
    tagIds: parseIds(query.read('tags')),
  });
  const page = ref(query.readPage());
  const loading = ref(true);
  const list = ref<LibraryListResponse | null>(null);
  const error = ref<string | null>(null);

  async function load(nextPage = page.value): Promise<void> {
    loading.value = true;
    error.value = null;

    const term = search.value.trim();
    // Only then does the whole result set arrive at once and paging move client-side.
    const fullSet = searchReturnsFullSet && term !== '';

    query.patch({
      search: term,
      sort: sortBy.value === 'newest' ? '' : sortBy.value,
      categories: filters.value.categoryIds.join(','),
      tags: filters.value.tagIds.join(','),
      // In full-set mode the page is client-side state belonging to the results
      // view, which owns the key instead; writing it here would erase it.
      ...(fullSet ? {} : { page: nextPage === 1 ? '' : String(nextPage) }),
    });

    try {
      list.value = await fetchList({
        search: term || undefined,
        sort: sortBy.value,
        categories: filters.value.categoryIds,
        tags: filters.value.tagIds,
        page: fullSet ? undefined : nextPage,
        perPage: fullSet ? undefined : DOCUMENT_LIST_PER_PAGE,
      });
      page.value = list.value.page;
    }
    catch (err) {
      list.value = null;

      if (isAxiosError(err) && err.response?.status === 403) {
        error.value = 'You do not have access to this section.';
      }
      else {
        error.value = 'Failed to load documents.';
      }
    }
    finally {
      loading.value = false;
    }
  }

  function onPageChange(nextPage: number): void {
    page.value = nextPage;
    void load(nextPage);
  }

  function reloadFromFirstPage(): void {
    page.value = 1;
    // A new question invalidates the view state that answered the previous one.
    query.patch({ page: '', view: '' });
    void load(1);
  }

  function reload(): void {
    void load(page.value);
  }

  onMounted(() => {
    void load();
  });

  return {
    search,
    sortBy,
    filters,
    page,
    loading,
    list,
    error,
    perPage: DOCUMENT_LIST_PER_PAGE,
    onPageChange,
    reloadFromFirstPage,
    reload,
  };
}

function parseSort(value: string): LibrarySortField {
  return SORT_FIELDS.find(field => field === value) ?? 'newest';
}

function parseIds(value: string): number[] {
  if (value === '') {
    return [];
  }

  return value
    .split(',')
    .map(part => Number.parseInt(part, 10))
    .filter(id => Number.isInteger(id) && id > 0);
}
